/**
 * Reaching the operator who is not looking at the CRM.
 *
 * Three signals, in order of how far they carry: the tab title (visible in the
 * tab strip and in the taskbar), the tab icon (visible when the strip is too
 * crowded for the title), and a system notice (visible from another
 * application entirely). The first two cost nothing and need no permission, so
 * they always happen; the third is behind a switch in the calls rail.
 *
 * Mounted from `IncomingCall`, which exists only while the phone is ringing -
 * so "mounted" and "ringing" are the same statement and there is no `active`
 * flag to get out of step with. That also keeps it correct without trying: only
 * the window holding the device lease is ever offered the call, so only that
 * window can raise a notice, and a notice raised by a window that is not
 * holding the phone would send the operator to the wrong screen.
 *
 * ⚠ The notice is NOT gated on the reduced-motion preference the ringtone reads.
 * An operator who has asked for less animation is precisely the operator with no
 * sound, and hiding the visual alert behind the same check would leave them with
 * nothing at all. A swapped title is not motion; a blinking one would be, which
 * is why the title is set once and left alone.
 */
import { useEffect, useRef } from "react";
import { useT } from "../../../i18n/I18nProvider";
import {
  RINGING_FAVICON,
  alertVerdict,
  readAlertEnabled,
  ringingTitle,
  type AlertPermission,
} from "./callAlert";

/** What the Notification API says right now, folded into our four values. */
export function alertPermission(): AlertPermission {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  const perm = Notification.permission;
  return perm === "granted" || perm === "denied" ? perm : "default";
}

/** One notice at a time: a replacement, never a second row in the tray. */
const TAG = "crm-incoming-call";

function faviconLink(): HTMLLinkElement | null {
  if (typeof document === "undefined") return null;
  const existing = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (existing) return existing;
  // The CRM ships one in `index.html`, so this is for the day somebody removes
  // it rather than a case seen in the wild. A ring with no icon to change still
  // has a title and a notice.
  const link = document.createElement("link");
  link.rel = "icon";
  document.head.appendChild(link);
  return link;
}

export interface CallAlertInput {
  /** The caller as the card names them - a company, a person, or "Unknown". */
  caller: string;
  /** The number, formatted, for the second line of the notice. */
  number: string;
}

export function useCallAlert({ caller, number }: CallAlertInput): void {
  const t = useT();
  const noticeRef = useRef<Notification | null>(null);

  // The title and the icon: no permission, no switch, every ring.
  useEffect(() => {
    const link = faviconLink();
    const iconWas = link?.getAttribute("href") ?? null;
    document.title = ringingTitle(caller, t("app.browserTitle"));
    link?.setAttribute("href", RINGING_FAVICON);

    return () => {
      // Restored from the catalogue rather than from a value captured on mount.
      // `I18nProvider` rewrites the title whenever the locale changes, so a
      // captured string would put the previous language back on a ring that
      // spanned a language switch.
      document.title = t("app.browserTitle");
      if (link) {
        if (iconWas) link.setAttribute("href", iconWas);
        else link.removeAttribute("href");
      }
    };
  }, [caller, t]);

  // The system notice: switched on, permitted, and this window not in front.
  useEffect(() => {
    const verdict = alertVerdict({
      permission: alertPermission(),
      enabled: readAlertEnabled(),
      // Read at the moment the call arrives. An operator who alt-tabs away
      // mid-ring keeps the card they can come back to; one who was already
      // elsewhere is the person this is for.
      focused: typeof document !== "undefined" && document.hasFocus(),
    });
    if (verdict !== "raise") return;

    try {
      // Re-raised when the caller's name arrives - the lookup lands a round trip
      // after the ring starts, so the first notice says "Unknown number" and
      // would otherwise keep saying it for the whole ring. The tag makes the
      // second one REPLACE the first instead of stacking beside it.
      const notice = new Notification(t("calls.alert.title"), {
        body: `${caller} - ${number}`,
        tag: TAG,
      });
      notice.onclick = () => {
        // Brings the CRM forward, which is the entire point of the notice: the
        // card with the answer button is already up in this window.
        window.focus();
        notice.close();
      };
      noticeRef.current = notice;
    } catch {
      // Some browsers carry the constructor and throw on use (iOS Safari). The
      // title and the icon above have already done their half.
    }

    return () => {
      // The caller can give up, or the call can be answered on a mobile, and
      // either way the offer is withdrawn. A notice left in the tray would
      // outlive the caller and send somebody to a phone that stopped ringing.
      noticeRef.current?.close();
      noticeRef.current = null;
    };
  }, [caller, number, t]);
}
